import axios from "axios";
import { useState } from "react";
import { useAppContext } from "../../../context/AuthContext";
import { ArticleController, ArticleControllerType } from "../../../controller/article/ArticleController";
import { AuthorController, AuthorControllerType } from "../../../controller/author/AuthorController";
import { KeywordsController, KeywordsControllerType } from "../../../controller/keywords/KeywordsController";
import { UserController, UserControllerType } from "../../../controller/user/UserController";
import LabelsContext from "../../../lang/LabelsContext";
import KeywordsDto from "../../../model/keywords/KeywordsDto";
import EnumReviewStatus from "../../../utils/enum/EnumReviewStatus";
import { getReviewStatus, handleErrorMessage, useEffectOnce } from "../../../utils/Utils";

interface SearchManuscriptsServiceType {
  value: any;
  setValue: any;
  handleChange: any;
  keywordsList: Array<KeywordsDto>;
  reviewStatusList: any;
  searchManuscripts: any;
  articles: any;
  typeList: any;
  authorList: any;
  associateEditorList: any;
  activeOptions: any;
  uplaodUserList: any;
  handleChangeActive: any;
  handleChangeReviewStatus: any;
}

const ACTIVE_TYPES = [
  EnumReviewStatus.DRAFT,
  EnumReviewStatus.MAJOR_REVISION,
  EnumReviewStatus.MINOR_REVISION,
  EnumReviewStatus.UNDER_REVIEW,
  EnumReviewStatus.REVIEWED,
  EnumReviewStatus.NEW_ARTICLE,
  EnumReviewStatus.REVIEWER_SELECTION,
  EnumReviewStatus.CORRECTION,
  EnumReviewStatus.ASSOCIATE_EDITOR,
  EnumReviewStatus.RETURNED_TO_EDITOR,
];

const INACTIVE_TYPES = [
  EnumReviewStatus.REJECTED,
  EnumReviewStatus.ACCEPTED,
  EnumReviewStatus.REJECTED_R0,
  EnumReviewStatus.DIRECT_REJECT,
  EnumReviewStatus.DIRECT_REJECT_R0,
  EnumReviewStatus.REJECT_AND_RESUBMIT_AS_A_LETTER_TO_THE_EDITOR,
];

export default function SearchManuscriptsService(): SearchManuscriptsServiceType {
  const { showMessage, setBlockUI } = useAppContext();
  const { Labels } = LabelsContext();
  const { axiosArticleSearch, axiosGetTypes, axiosGetUploadUserList }: ArticleControllerType = ArticleController();
  const { axiosGetKeywordsList }: KeywordsControllerType = KeywordsController();
  const { axiosGetAssociateEditorList }: UserControllerType = UserController();
  const { axiosGetShortAuthorList }: AuthorControllerType = AuthorController();

  const [typeList, setTypeList] = useState<KeywordsDto[]>([]);
  const [keywordsList, setKeywordsList] = useState<KeywordsDto[]>([]);
  const [articles, setListArticles] = useState<any>();
  const [authorList, setAuthorList] = useState<any>();
  const [uplaodUserList, setUploadUserList] = useState<any>();
  const [associateEditorList, setAssociateEditorList] = useState<any>();

  const reviewStatusList = [
    { title: getReviewStatus(EnumReviewStatus.DRAFT, Labels), id: EnumReviewStatus.DRAFT },
    { title: getReviewStatus(EnumReviewStatus.NEW_ARTICLE, Labels), id: EnumReviewStatus.NEW_ARTICLE },
    { title: getReviewStatus(EnumReviewStatus.REJECTED, Labels), id: EnumReviewStatus.REJECTED },
    { title: getReviewStatus(EnumReviewStatus.ACCEPTED, Labels), id: EnumReviewStatus.ACCEPTED },
    { title: getReviewStatus(EnumReviewStatus.MAJOR_REVISION, Labels), id: EnumReviewStatus.MAJOR_REVISION },
    { title: getReviewStatus(EnumReviewStatus.MINOR_REVISION, Labels), id: EnumReviewStatus.MINOR_REVISION },
    { title: getReviewStatus(EnumReviewStatus.REVIEWED, Labels), id: EnumReviewStatus.REVIEWED },
    { title: getReviewStatus(EnumReviewStatus.UNDER_REVIEW, Labels), id: EnumReviewStatus.UNDER_REVIEW },
    { title: getReviewStatus(EnumReviewStatus.REVIEWER_SELECTION, Labels), id: EnumReviewStatus.REVIEWER_SELECTION },
    { title: getReviewStatus(EnumReviewStatus.DIRECT_REJECT, Labels), id: EnumReviewStatus.DIRECT_REJECT },
    { title: getReviewStatus(EnumReviewStatus.CORRECTION, Labels), id: EnumReviewStatus.CORRECTION },
    { title: getReviewStatus(EnumReviewStatus.REJECTED_R0, Labels), id: EnumReviewStatus.REJECTED_R0 },
    { title: getReviewStatus(EnumReviewStatus.DIRECT_REJECT_R0, Labels), id: EnumReviewStatus.DIRECT_REJECT_R0 },
    {
      title: getReviewStatus(EnumReviewStatus.REJECT_AND_RESUBMIT_AS_A_LETTER_TO_THE_EDITOR, Labels),
      id: EnumReviewStatus.REJECT_AND_RESUBMIT_AS_A_LETTER_TO_THE_EDITOR,
    },
    { title: getReviewStatus(EnumReviewStatus.ASSOCIATE_EDITOR, Labels), id: EnumReviewStatus.ASSOCIATE_EDITOR },
    { title: getReviewStatus(EnumReviewStatus.RETURNED_TO_EDITOR, Labels), id: EnumReviewStatus.RETURNED_TO_EDITOR },
  ];

  const [value, setValue] = useState<any>({
    title: undefined,
    keyword: undefined,
    reviewStatus: [],
    type: undefined,
    author: undefined,
    articleId: undefined,
    associateEditorId: undefined,
    activeArticle: null,
    uploadBy: undefined,
    dateFrom: undefined,
    dateTo: undefined,
  });

  useEffectOnce(() => {
    loadData();
  });

  const searchManuscripts = () => {
    setBlockUI(true);

    const statusListParam = value.reviewStatus.map((x: any) => x.id);
    const searchData = {
      title: value.title,
      keyword: value.keyword?.keyword,
      statusList: statusListParam.length > 0 ? statusListParam : undefined,
      authorId: value.author?.id,
      articleId: value.articleId,
      associateEditorId: value.associateEditorId?.id,
      typeId: value.type?.id,
      uploadUserId: value.uploadBy?.id,
      dateFrom: value.dateFrom,
      dateTo: value.dateTo,
    };

    axiosArticleSearch(searchData)
      .then((result: any) => {
        setListArticles(result.data);
        setBlockUI(false);
      })
      .catch((error: any) => {
        setBlockUI(false);
        handleErrorMessage(showMessage, error);
      });
  };

  const loadData = () => {
    setBlockUI(true);
    axios
      .all([
        axiosGetKeywordsList(),
        axiosGetTypes(),
        axiosGetShortAuthorList(),
        axiosGetAssociateEditorList(),
        axiosGetUploadUserList(),
      ])
      .then(
        axios.spread(
          (resposneKeywordsList, responseTypeList, responseAuthorList, responseAssociateEditor, responseUploadUser) => {
            setKeywordsList(resposneKeywordsList.data);
            setTypeList(responseTypeList.data);
            setAuthorList(responseAuthorList.data);
            setAssociateEditorList(responseAssociateEditor.data);
            setUploadUserList(responseUploadUser.data);
            setValue({
              ...value,
              reviewStatus: reviewStatusList,
              activeArticle: { name: "All", id: 0 },
            });
            setBlockUI(false);
          }
        )
      )
      .catch((error: any) => {
        setBlockUI(false);
        handleErrorMessage(showMessage, error);
      });
  };

  const handleChange = (e: any) => setValue({ ...value, [e.target.name]: e.target.value });

  const handleChangeActive = (valueOne: any) => {
    if (valueOne !== null && valueOne.id !== 3) {
      let reviewStatusSelected: any = [];
      if (valueOne.id === 0) {
        reviewStatusSelected = reviewStatusList;
      } else if (valueOne.id === 1) {
        reviewStatusSelected = reviewStatusList.filter((x: any) => ACTIVE_TYPES.includes(x.id));
      } else if (valueOne.id === 2) {
        reviewStatusSelected = reviewStatusList.filter((x: any) => INACTIVE_TYPES.includes(x.id));
      }
      setValue({
        ...value,
        activeArticle: valueOne,
        reviewStatus: reviewStatusSelected,
      });
    } else {
      setValue({
        ...value,
        activeArticle: valueOne,
      });
    }
  };

  const handleChangeReviewStatus = (valueOne: any) => {
    setValue({
      ...value,
      activeArticle: { name: Labels.CUSTOM, id: 3 },
      reviewStatus: valueOne,
    });
  };

  const activeOptions = [
    { name: "All", id: 0 },
    { name: Labels.ACTIVE, id: 1 },
    { name: Labels.INACTIVE, id: 2 },
    { name: Labels.CUSTOM, id: 3 },
  ];

  return {
    value,
    setValue,
    handleChange,
    keywordsList,
    reviewStatusList,
    searchManuscripts,
    articles,
    typeList,
    authorList,
    associateEditorList,
    activeOptions,
    uplaodUserList,
    handleChangeActive,
    handleChangeReviewStatus,
  };
}

export type { SearchManuscriptsServiceType };
